import "../../../styles/executive-summary.css";

import { Alert, Button } from "antd";
import {
  COMPANY_TOP_8,
  FREIGHT_METRICS,
  INBOUND_SUMMARY,
  KPI_CARDS,
  OUTSTANDING_BALANCE,
  PACKAGE_TYPE_SUMMARY,
  SHIPMENT_STATUS,
  SHIPMENT_TOTAL,
  SHIPMENT_TREND,
} from "./mockData";
import { KpisSkeleton, MetricsSkeleton, OperationsSkeleton, SummariesSkeleton } from "./components/DashboardSkeleton";
import { adaptKpis, adaptMetrics, adaptOperations, adaptSummaries } from "./adaptExecutiveSummary";
import {
  getDashboardKpis,
  getDashboardMetrics,
  getDashboardOperations,
  getDashboardSummaries,
} from "../../services/dashboard";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import ChartsSection from "./components/ChartsSection";
import DashboardFilterBar from "./components/DashboardFilterBar";
import DashboardHeader from "./components/DashboardHeader";
import KpiCards from "./components/KpiCards";
import MetricCards from "./components/MetricCards";
import SummaryPanel from "./components/SummaryPanel";
import { buildDrilldownSearch } from "./drilldownParams";
import { clampDayToMonth } from "./dashboardDay";
import dayjs from "dayjs";
import { getWareHouses } from "../../services/originPackage";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { useDashboardSection } from "./useDashboardSection";

// fallback ต่อ section (รูปแบบ adapted เดียวกับ output ของ adapter) — section พังแล้วหน้าไม่ว่าง
const FALLBACK_SUMMARIES = { inboundSummary: INBOUND_SUMMARY, packageTypeSummary: PACKAGE_TYPE_SUMMARY };
const FALLBACK_KPIS = { kpiCards: KPI_CARDS };
const FALLBACK_METRICS = { outstanding: OUTSTANDING_BALANCE, freight: FREIGHT_METRICS };
const FALLBACK_OPERATIONS = {
  shipmentStatus: SHIPMENT_STATUS,
  shipmentTotal: SHIPMENT_TOTAL,
  shipmentTrend: SHIPMENT_TREND,
  companyTop: COMPANY_TOP_8,
};

// แถบ error เล็ก ๆ ต่อ section — พังเฉพาะส่วน ไม่ลามทั้งหน้า
const SectionError = ({ hasData, loading, onRetry }) => {
  const { t } = useTranslation("dashboard");

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={hasData ? t("Error.Section_Stale") : t("Error.Section_Failed")}
      action={
        <Button size="small" onClick={onRetry} loading={loading}>
          {t("Error.Retry")}
        </Button>
      }
    />
  );
};

// wrapper จัดการ loading(skeleton) / fetching(dim) / error(retry) + fallback ให้ทุก section เหมือนกัน
const Section = ({ state, fallback, skeleton, children }) => {
  const { data, loading, fetching, error, refetch } = state;
  if (loading) return skeleton;
  const view = data ?? fallback;
  return (
    <div className={fetching ? "is-refetching" : ""}>
      {error && <SectionError hasData={!!data} loading={fetching} onRetry={refetch} />}
      {children(view)}
    </div>
  );
};

// default date filter = วันที่ 1 ของเดือนปัจจุบัน → วันนี้ (เช่น 2026-06-01 → 2026-06-23)
// ตัวเลข KPI/Freight จึงเป็น "เดือนนี้ถึงปัจจุบัน" ให้ตรงช่วงกับ % vs Last Month
const getDefaultDateRange = () => [dayjs().startOf("month").format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")];

// ตัวเลือก "ทุกคลัง" — label แปลตามภาษา ส่วน value คงที่ (`"all"` = ไม่ส่ง warehouse_id)
const allWarehouseOption = (t) => ({ label: t("Filter.All_Warehouse"), value: "all" });

// warehouse ที่ dashboard ไม่รวม — ให้ตรงกับ DashboardService.EXCLUDED_WAREHOUSE_CODES ฝั่ง BE
const EXCLUDED_WAREHOUSE_CODES = new Set(["SZ"]);

const Home = () => {
  const { accessToken } = useAccessToken();
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const location = useLocation();

  // กลับมาจากหน้า drill-down → คืนค่า filter ที่ค้างไว้ (requirement 2026-07-28 step 1 ข้อ 4)
  // อ่านครั้งเดียวตอน mount ผ่าน lazy initializer — เปลี่ยน filter ต่อจากนี้เป็นของ state ปกติ
  const restored = location.state?.dashboardFilter;

  const [year, setYear] = useState(() => restored?.year ?? 2026);
  const [month, setMonth] = useState(() => restored?.month ?? dayjs().month() + 1); // 1-12, default เดือนปัจจุบัน
  const [day, setDay] = useState(() => restored?.day ?? dayjs().date()); // 1-31, default วันปัจจุบัน — คุมคอลัมน์แรกของ Summary
  const [dateRange, setDateRange] = useState(() => restored?.dateRange ?? getDefaultDateRange());
  const [warehouse, setWarehouse] = useState(() => restored?.warehouse ?? "all");
  // เก็บเฉพาะคลังที่โหลดมาได้ — ตัวเลือก "ทุกคลัง" ต่อหัวตอน render เพื่อให้ label เปลี่ยนตามภาษาได้
  const [originWarehouses, setOriginWarehouses] = useState([]);
  const warehouseOptions = [allWarehouseOption(t), ...originWarehouses.map((w) => ({ ...w, label: t("Filter.Warehouse", { code: w.code }) }))];

  // โหลด origin warehouse จริงมาเป็นตัวเลือก filter — value เป็น id ตัวเลข
  // พอเลือกแล้ว warehouseId ใน useDashboardSection เปลี่ยน → ยิง API ใหม่ทันที
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getWareHouses(accessToken);
        if (cancelled || !Array.isArray(data)) return;
        const origin = data
          .filter((w) => w.is_origin && !EXCLUDED_WAREHOUSE_CODES.has(w.warehouse_code?.toUpperCase()))
          .map((w) => ({ value: w.id, code: w.warehouse_code }));
        setOriginWarehouses(origin);
      } catch (err) {
        console.error("[Home] fetchWarehouseOptions: ", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const params = { year, dateRange, warehouse };
  // Summary อยู่เหนือ filter bar — คุมด้วย year/month/day จาก header เท่านั้น
  // จึงไม่ส่ง dateRange/warehouse: กดเลือกที่ filter bar แล้ว section นี้ไม่ reload และไม่ถูกกรอง
  // (BE computeSummaries ใช้แค่ selectedDay/month/year range — ไม่ใช้ start/end อยู่แล้ว)
  // month คุมคอลัมน์ "Month Total" · day คุมคอลัมน์แรก (Today) — section อื่นไม่ส่งทั้งคู่
  const summaries = useDashboardSection(getDashboardSummaries, adaptSummaries, { year, month, day });
  const kpis = useDashboardSection(getDashboardKpis, adaptKpis, params);
  const metrics = useDashboardSection(getDashboardMetrics, adaptMetrics, params);
  const operations = useDashboardSection(getDashboardOperations, adaptOperations, params);

  // section ใต้ filter bar — ไม่รวม Summary (Summary คุม refresh/last updated ที่ header)
  const filterSections = [kpis, metrics, operations];

  // เวลาโหลดข้อมูลสำเร็จล่าสุดเฉพาะ section ใต้ filter bar — refetch ตาม filter เมื่อไหร่ ค่านี้ขยับตาม
  const lastUpdated = filterSections.reduce(
    (latest, section) => (section.updatedAt && (!latest || section.updatedAt > latest) ? section.updatedAt : latest),
    null
  );

  // กด icon refresh ที่ filter bar → reload เฉพาะ section ใต้ filter bar ด้วยเงื่อนไข filter ปัจจุบัน
  const refreshing = filterSections.some((section) => section.fetching);
  const handleRefreshFilterSections = () => {
    filterSections.forEach((section) => section.refetch());
  };

  // เปลี่ยนปี/เดือน → วันที่ที่เลือกต้องไม่เกินจำนวนวันของเดือนใหม่ (เลือก 31 แล้วสลับไป ก.พ. → 28/29)
  const handleYearChange = (nextYear) => {
    setYear(nextYear);
    setDay((current) => clampDayToMonth(current, nextYear, month));
  };
  const handleMonthChange = (nextMonth) => {
    setMonth(nextMonth);
    setDay((current) => clampDayToMonth(current, year, nextMonth));
  };

  // ส่ง condition ไปทาง query string ทั้งชุด (year/month/day/period ใช้ query จริง · date/warehouse ถือไปเพื่อคืนค่าตอนกดกลับ)
  // `period` = ช่องที่ผู้ใช้กด (วัน/เดือน/ปี) → หน้าปลายทางเห็นตัวเลขของช่วงเดียวกับที่กดมา (requiment 2026-08-10)
  const drilldownSearch = (period) => buildDrilldownSearch({ year, month, day, dateRange, warehouse, period });

  // ทั้ง 2 ลิงก์เปิดแท็บใหม่ → dashboard ที่ตั้ง filter ไว้ยังอยู่ที่แท็บเดิม ไม่ต้องกดกลับ
  const inboundCompaniesHref = (period) => `/home/inbound-summary/company?${drilldownSearch(period)}`; // step 1: ทุกบริษัท
  const companyDetailHref = (companyCode, period) =>
    `/home/inbound-summary/company/${encodeURIComponent(companyCode)}?${drilldownSearch(period)}`; // step 2: ลูกค้า

  const handleClearHeaderFilter = () => {
    setYear(2026);
    setMonth(dayjs().month() + 1);
    setDay(dayjs().date());
  };
  const handleClearDashboardFilter = () => {
    setDateRange(getDefaultDateRange());
    setWarehouse("all");
  };

  return (
    <div className="executive-summary">
      <DashboardHeader
        year={year}
        month={month}
        day={day}
        lastUpdated={summaries.updatedAt}
        refreshing={summaries.fetching}
        onRefresh={summaries.refetch}
        onYearChange={handleYearChange}
        onMonthChange={handleMonthChange}
        onDayChange={setDay}
        onClearFilter={handleClearHeaderFilter}
      />

      <Section state={summaries} fallback={FALLBACK_SUMMARIES} skeleton={<SummariesSkeleton />}>
        {(view) => (
          <>
            <SummaryPanel
              {...view.inboundSummary}
              keyHeaderHref={inboundCompaniesHref}
              rowNameHref={companyDetailHref}
            />
            <SummaryPanel {...view.packageTypeSummary} />
          </>
        )}
      </Section>

      <DashboardFilterBar
        dateRange={dateRange}
        warehouse={warehouse}
        warehouseOptions={warehouseOptions}
        lastUpdated={lastUpdated}
        refreshing={refreshing}
        onRefresh={handleRefreshFilterSections}
        onDateChange={setDateRange}
        onWarehouseChange={setWarehouse}
        onClearFilter={handleClearDashboardFilter}
      />

      <div className="Dashboard">
        <Section state={kpis} fallback={FALLBACK_KPIS} skeleton={<KpisSkeleton />}>
          {(view) => <KpiCards items={view.kpiCards} />}
        </Section>

        <Section state={metrics} fallback={FALLBACK_METRICS} skeleton={<MetricsSkeleton />}>
          {(view) => <MetricCards outstanding={view.outstanding} sea={view.freight.sea} land={view.freight.land} />}
        </Section>
      </div>

      <Section state={operations} fallback={FALLBACK_OPERATIONS} skeleton={<OperationsSkeleton />}>
        {(view) => (
          <ChartsSection
            shipmentStatus={view.shipmentStatus}
            shipmentTotal={view.shipmentTotal}
            trend={view.shipmentTrend}
            companies={view.companyTop}
          />
        )}
      </Section>
    </div>
  );
};

export default Home;
